import React, { useState, useEffect, useCallback } from "react";
import {
  AuditLog,
  AuditStats,
  getUserAuditLogs,
  getAuditStatistics,
  formatAuditLog,
} from "../services/auditService";
import { AuditLogTable } from "../components/audit/AuditLogTable";
import { AuditFilters } from "../components/audit/AuditFilters";
import { AuditStats as AuditStatsComponent } from "../components/audit/AuditStats";
import { AuditPagination } from "../components/audit/AuditPagination";
import { PageHeader } from "@/components/layout/PageHeader";
import { Activity } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface Filters {
  search: string;
  actionType: string;
  startDate: string;
  endDate: string;
}

export function AuditPage() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [stats, setStats] = useState<AuditStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    limit: 5,
    offset: 0,
    total: 0,
  });
  const [filters, setFilters] = useState<Filters>({
    search: "",
    actionType: "",
    startDate: "",
    endDate: "",
  });
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [showLogModal, setShowLogModal] = useState(false);

  // Carregar dados iniciais
  useEffect(() => {
    loadData();
    loadStats();
  }, []);

  // Carregar logs quando filtros ou paginação mudarem
  useEffect(() => {
    loadData();
  }, [filters, pagination.offset]);

  const loadData = async () => {
    try {
      setLoading(true);

      const apiFilters = {
        limit: pagination.limit,
        offset: pagination.offset,
        actionType: filters.actionType || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      };

      const response = await getUserAuditLogs(apiFilters);

      if (response.success) {
        let filteredLogs = response.data.logs;

        // Aplicar filtro de busca no frontend (para simplicidade)
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          filteredLogs = filteredLogs.filter(
            (log) =>
              log.user_name.toLowerCase().includes(searchLower) ||
              log.user_email.toLowerCase().includes(searchLower) ||
              log.action.toLowerCase().includes(searchLower)
          );
        }

        setLogs(filteredLogs);
        setPagination((prev) => ({
          ...prev,
          total: response.data.pagination.total,
        }));
      }
    } catch (error) {

    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      setStatsLoading(true);
      const response = await getAuditStatistics();

      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {

    } finally {
      setStatsLoading(false);
    }
  };

  const handleFiltersChange = useCallback((newFilters: Filters) => {
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, offset: 0 })); // Reset para primeira página
  }, []);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({
      ...prev,
      offset: (page - 1) * prev.limit,
    }));
  };

  const handleLogClick = (log: AuditLog) => {
    setSelectedLog(log);
    setShowLogModal(true);
  };

  const closeLogModal = () => {
    setShowLogModal(false);
    setSelectedLog(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <PageHeader
        icon={Activity}
        title="Auditoria"
        description="Histórico de ações dos usuários na plataforma"
        onBack={() => navigate("/")}
      />

      <div className="container mx-auto px-6 py-6">
        {/* Estatísticas */}
        {stats && <AuditStatsComponent stats={stats} loading={statsLoading} />}

        {/* Log de Atividades */}
        <div className="mb-6">
          <div className="flex items-center mb-4">
            <div className="w-8 h-8 bg-purple-100 rounded-md flex items-center justify-center mr-3">
              <svg
                className="w-5 h-5 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Log de Atividades
              </h2>
              <p className="text-sm text-gray-500">
                Registros detalhados de todas as ações realizadas pelos usuários
              </p>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <AuditFilters onFiltersChange={handleFiltersChange} loading={loading} />

        {/* Tabela */}
        <AuditLogTable
          logs={logs}
          loading={loading}
          onLogClick={handleLogClick}
        />

        {/* Paginação */}
        {pagination.total > pagination.limit && (
          <AuditPagination
            pagination={pagination}
            onPageChange={handlePageChange}
            loading={loading}
          />
        )}

        {/* Modal de Detalhes do Log */}
        {showLogModal && selectedLog && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Detalhes da Ação
                  </h3>
                  <button
                    onClick={closeLogModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Usuário
                    </label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedLog.user_name} ({selectedLog.user_email})
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Ação
                    </label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedLog.action}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Tipo
                    </label>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        formatAuditLog(selectedLog).actionTypeColor
                      }`}
                    >
                      <span className="mr-1">
                        {formatAuditLog(selectedLog).actionTypeIcon}
                      </span>
                      {selectedLog.action_type_name}
                    </span>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Data/Hora
                    </label>
                    <p className="mt-1 text-sm text-gray-900">
                      {formatAuditLog(selectedLog).formattedDate}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      IP
                    </label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedLog.ip_address}
                    </p>
                  </div>

                  {selectedLog.details && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Detalhes
                      </label>
                      <pre className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md overflow-auto">
                        {JSON.stringify(
                          formatAuditLog(selectedLog).parsedDetails,
                          null,
                          2
                        )}
                      </pre>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={closeLogModal}
                    className="bg-purple-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
